import { useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { useEffect, useRef } from 'react';

import { V4A_NAMESPACE } from '../constants';
import type { SignupFormProps } from './signup-form';
import { SignupForm } from './signup-form';

interface TrialDialogProps extends Omit<SignupFormProps, 'idPrefix'> {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The source's `#np-trial-dialog`: every trial CTA opens the enrolment card in a native modal dialog.
 * The source moved the hero form into the dialog; here the dialog mounts its own card while open (so its
 * captcha only loads when it is needed). Focus returns to whatever opened it.
 */
export function TrialDialog({ isOpen, onClose, ...formProps }: TrialDialogProps) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previousFocus = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || typeof dialog.showModal !== 'function') return undefined;
    if (!isOpen) {
      if (dialog.open) dialog.close();
      return undefined;
    }
    previousFocus.current = document.activeElement;
    if (!dialog.open) dialog.showModal();
    const focusTimer = window.setTimeout(() => {
      dialog.querySelector<HTMLInputElement>('input:not([type=hidden])')?.focus({ preventScroll: true });
    }, 50);
    return () => {
      window.clearTimeout(focusTimer);
      const previous = previousFocus.current;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    // Esc (cancel) and any other close() path keep React's state in step with the element.
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) onClose();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- backdrop click closes the modal; Esc and the close button are the keyboard paths
    <dialog ref={dialogRef} className='np-trial-dialog' id='np-trial-dialog' aria-label={t('dialog.label')} onClick={handleBackdropClick}>
      <div className='np-trial-dialog__panel'>
        <button className='np-trial-dialog__close' type='button' aria-label={t('dialog.close')} onClick={onClose}>
          ×
        </button>
        <div className='np-trial-dialog__body'>{isOpen && <SignupForm idPrefix='nd' {...formProps} />}</div>
      </div>
    </dialog>
  );
}
